import React from 'react';
import {
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Tooltip,
  Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../app/utils/formatters';
import { Order } from '../../../app/data/models/orders/order';
import { Payment } from '../../../app/data/models/payments/payment';
import { format } from 'date-fns';
import PaymentIcon from '@mui/icons-material/Payment';
import RefundIcon from '@mui/icons-material/MoneyOff';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InfoIcon from '@mui/icons-material/Info';
import { PaymentMethod } from '../../../app/data/models/payments/paymentMethod';

interface PaymentHistoryProps {
  order: Order;
  onViewPaymentDetails: (payment: Payment) => void;
  onPrintPaymentReceipt: (paymentId: string) => void;
  onPrintRefundReceipt: (refundId: string) => void;
  getPaymentMethodDisplay: (method: PaymentMethod) => string;
}

const PaymentHistory: React.FC<PaymentHistoryProps> = ({
  order,
  onViewPaymentDetails,
  onPrintPaymentReceipt,
  onPrintRefundReceipt,
  getPaymentMethodDisplay,
}) => {
  const { t } = useTranslation();

  // Sort payments by date (newest first)
  const sortedPayments = [...(order.payments || [])].sort((a, b) => {
    return (
      new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()
    );
  });

  return (
    <Grid item xs={12}>
      <Typography variant="h6" gutterBottom>
        {t('biller.payment-history')}
      </Typography>
      <Paper variant="outlined">
        {sortedPayments.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('biller.no-payment-history')}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {sortedPayments.map((payment) => {
              // Check if payment has any refunds
              const refunds = order.refunds?.filter(
                (r) => r.paymentId === payment.id
              );
              const hasRefunds = refunds && refunds.length > 0;
              const totalRefunded =
                refunds?.reduce((total, refund) => total + refund.amount, 0) ||
                0;
              const isFullyRefunded = totalRefunded >= payment.amount;
              const isPartiallyRefunded =
                totalRefunded > 0 && totalRefunded < payment.amount;

              return (
                <ListItem
                  key={payment.id}
                  divider
                  secondaryAction={
                    <Box sx={{ display: 'flex' }}>
                      <Tooltip title={t('biller.print-receipt')}>
                        <IconButton
                          edge="end"
                          onClick={() => onPrintPaymentReceipt(payment.id)}
                          size="small"
                        >
                          <ReceiptIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('biller.view-details')}>
                        <IconButton
                          edge="end"
                          onClick={() => onViewPaymentDetails(payment)}
                          size="small"
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemIcon>
                    <PaymentIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1">
                          {formatCurrency(payment.amount)}
                        </Typography>
                        {isFullyRefunded && (
                          <Chip
                            size="small"
                            color="error"
                            label={t('biller.fully-refunded')}
                            sx={{ ml: 1 }}
                          />
                        )}
                        {isPartiallyRefunded && (
                          <Chip
                            size="small"
                            color="warning"
                            label={t('biller.partially-refunded')}
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="span"
                        >
                          {getPaymentMethodDisplay(payment.paymentMethod)}
                          {' • '}
                          {format(new Date(payment.paymentDate), 'PPp')}
                        </Typography>
                        {hasRefunds && (
                          <Box sx={{ mt: 1 }}>
                            {refunds?.map((refund) => (
                              <Box
                                key={refund.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  ml: 2,
                                  mt: 0.5,
                                }}
                              >
                                <RefundIcon
                                  color="error"
                                  fontSize="small"
                                  sx={{ mr: 1 }}
                                />
                                <Typography
                                  variant="body2"
                                  color="error.main"
                                  sx={{ mr: 1 }}
                                >
                                  {formatCurrency(refund.amount)}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {refund.refundDate &&
                                    format(new Date(refund.refundDate), 'PPp')}
                                </Typography>
                                <Tooltip title={t('biller.print-receipt')}>
                                  <IconButton
                                    edge="end"
                                    onClick={() =>
                                      onPrintRefundReceipt(refund.id)
                                    }
                                    size="small"
                                  >
                                    <ReceiptIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>
    </Grid>
  );
};

export default PaymentHistory;
